import { useState, useRef, useEffect, useCallback } from 'react';

const SESSION_KEY = 'osho_chat_session';
const API = process.env.REACT_APP_BACKEND_URL;

export function useChat({ loadHistory = false } = {}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const sessionRef = useRef(typeof window !== 'undefined' ? localStorage.getItem(SESSION_KEY) : null);

  useEffect(() => {
    if (loadHistory && sessionRef.current) {
      fetch(`${API}/api/chat/history/${sessionRef.current}`)
        .then((r) => (r.ok ? r.json() : []))
        .then((h) => { if (Array.isArray(h) && h.length) setMessages(h.map((m) => ({ role: m.role, content: m.content }))); })
        .catch(() => {});
    }
  }, [loadHistory]);

  const send = useCallback(async (text) => {
    const msg = (text || '').trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    setLoading(true);
    const setLastAssistant = (updater) => setMessages((m) => {
      const c = [...m];
      const idx = c.length - 1;
      c[idx] = { role: 'assistant', content: updater(c[idx].content) };
      return c;
    });
    try {
      const res = await fetch(`${API}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg, website: '' }),
      });
      if (!res.ok || !res.body) {
        const e = await res.json().catch(() => ({}));
        setLastAssistant(() => e.detail || 'The guide is resting. Please try again shortly.');
        setLoading(false);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const ev = JSON.parse(line.slice(5).trim());
            if (ev.session_id) { sessionRef.current = ev.session_id; localStorage.setItem(SESSION_KEY, ev.session_id); }
            if (ev.t) setLastAssistant((prev) => prev + ev.t);
            if (ev.error) setLastAssistant((prev) => prev || ev.error);
          } catch { /* ignore malformed chunk */ }
        }
      }
    } catch {
      setLastAssistant((prev) => prev || 'The guide is resting. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const reset = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    sessionRef.current = null;
    setMessages([]);
  }, []);

  return { messages, loading, send, reset };
}

export default useChat;
